﻿import { useEffect, useRef, useState } from 'react';

import { PickReports } from '../../../models/pickReports/IPickReports';
import { IPickReportQueryAdditional, IPickReportQueryExtended, FetchPickReportsFunction, FetchPickReports, PickReportUrl } from '../../../models/pickReports/IFetchPickReports';
import { FetchPickReportSpacesFunc,  SpacesUrl } from '../../../models/pickReports/IFetchPickReportSpaces';

export const usePickReportsFetcher = (
    query: IPickReportQueryExtended | undefined,
    pickReportUrl: PickReportUrl,
    spacesUrl: SpacesUrl | undefined,
    externalLayerUrl: string | undefined,
    urlParams: IPickReportQueryAdditional
) => {

    const [loading, setLoading] = useState<boolean>();
    const [reports, setReports] = useState<PickReports>();

    const pickReportUrlRef = useRef<FetchPickReportsFunction>();
    const spacesUrlRef = useRef<FetchPickReportSpacesFunc>();
    const urlParamsRef = useRef<IPickReportQueryAdditional>();

    useEffect(() => {
        if (typeof pickReportUrl === 'string')
            pickReportUrlRef.current = (...data) => new FetchPickReports(pickReportUrl).getReports(...data);
        else if (pickReportUrl instanceof Function)
            pickReportUrlRef.current = pickReportUrl;
        else
            pickReportUrlRef.current = (...data) => pickReportUrl.getReports(...data);
    }, [pickReportUrl]);

    useEffect(() => {
        if (spacesUrl instanceof Function)
            spacesUrlRef.current = spacesUrl;
        else if (spacesUrl)
            spacesUrlRef.current = (...data) => spacesUrl.getSpaces(...data);
    }, [spacesUrl]);

    useEffect(() => {
        urlParamsRef.current = urlParams;
    }, [urlParams]);

    useEffect(() => {
        if (query && pickReportUrlRef.current && urlParamsRef.current) {
            const getReports = pickReportUrlRef.current;
            const getSpaces = spacesUrlRef.current;
            const params = urlParamsRef.current;

            const asyncFetch = async () => {

                const result: PickReports = {
                    depth: query.depth
                };

                setLoading(true);
                try {
                    result.objects = await getReports(query, params);
                    if (query.spaceFeatures?.length && getSpaces) {
                        const ids = new Set<number>();
                        query.spaceFeatures.forEach(({ id }) => {
                            if (id !== undefined)
                                ids.add(id);
                        })

                        result.spaces = await getSpaces(Array.from(ids), query.spaceFeatures);
                    }
                    if (externalLayerUrl &&  externalLayerUrl?.length > 0) {
                        await fetch(externalLayerUrl)
                            .then((response) => response.text())
                            .then((html) => {
                                //// see TRAC-924: Redo layer names and increase font size of the table
                                //// remove unnecessary metadata in title e.g. replace "FeatureInfoCollection - layer name: 'N2K 2006 vector'" by "N2K 2006 vector"
                                html = html.replace(/(<h5>).*'(.*)'.*(<\/h5>)/gm, "$1$2$3");
                                html = html.replace(/<\/style>/gm, "table { font-size: 14px !important; }</style>");

                                result.htmlString = html;
                            });
                    }
                }
                finally {
                    setLoading(false);
                    setReports(result)
                }
            }

            asyncFetch();
        }

    }, [query, externalLayerUrl]);

    return { loading, reports };
}